'use client';

import type { ComponentProps } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import type { AreaResponse } from '@/src/lib/api';
import { DEFAULT_MAP_VIEWPORT_SIZE, parseMapPositionFromSearchParams } from '@/src/lib/map/viewportBounds';
import { CurrentAreaHydrator } from '../_srp/_components/CurrentAreaHydrator';
import { MapStateHydrator } from '../_srp/_components/MapStateHydrator';

type Props = Omit<
  ComponentProps<typeof MapStateHydrator>,
  'initialShowBorder' | 'hasExplicitBorderParam' | 'waitForStreamedBoundary'
> & {
  area: AreaResponse;
  originPathname: string;
};

/**
 * Mounts removed-building area and map fallbacks together only on their originating HDP.
 * The shared search layout survives navigation, so unmounting both prevents stale fallback
 * state on other routes and remounting both restores a consistent area after browser Back.
 */
export function HdpFallbackMapStateHydrator({ area, originPathname, ...props }: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const borderParam = searchParams.get('border');
  const urlPosition = parseMapPositionFromSearchParams(new URLSearchParams(searchParams), DEFAULT_MAP_VIEWPORT_SIZE);

  if (pathname !== originPathname) {
    return null;
  }

  return (
    <>
      <CurrentAreaHydrator area={area} />
      <MapStateHydrator
        {...props}
        initialMapPosition={urlPosition ?? props.initialMapPosition}
        initialShowBorder={borderParam !== 'false'}
        hasExplicitBorderParam={borderParam !== null}
        waitForStreamedBoundary={false}
      />
    </>
  );
}
